import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Clock3, X, ShieldCheck } from "lucide-react";
import { DEMO_PRODUCTS } from "../../data/content";
import { EASE } from "./primitives";

export const openDemoChooser = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("ajmus:book-demo"));
  }
};

// AJMUS-WEB-DEMO-PLATFORM-UI-P1
// Compact desktop layout: keeps the title, four product cards and demo notice
// visible together on common laptop viewports while retaining safe scrolling on
// smaller screens. The content scroller is reset each time the chooser opens.
export default function DemoBookingModal() {
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("ajmus:book-demo", show);
    return () => window.removeEventListener("ajmus:book-demo", show);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    const frame = window.requestAnimationFrame(() => {
      if (contentRef.current) contentRef.current.scrollTop = 0;
    });

    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ajmus-navy/75 px-3 py-3 backdrop-blur-md sm:px-5 sm:py-5"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ajmus-demo-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.985 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="relative flex max-h-[calc(100vh-1.5rem)] w-full max-w-[1180px] flex-col overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#FCFCFD] shadow-[0_35px_120px_rgba(0,0,0,0.42)] sm:max-h-[calc(100vh-2.5rem)]"
          >
            <div className="relative shrink-0 overflow-hidden rounded-t-[1.75rem] bg-ajmus-navy px-5 py-5 text-white sm:px-7 sm:py-6">
              <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-health/25 blur-[100px]" />
              <div className="pointer-events-none absolute -bottom-20 left-16 h-56 w-56 rounded-full bg-edu/25 blur-[100px]" />
              <button
                onClick={() => setOpen(false)}
                className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/80 transition hover:bg-white/15 hover:text-white sm:right-5 sm:top-5"
                aria-label="Close demo chooser"
              >
                <X size={17} />
              </button>

              <div className="relative max-w-3xl pr-12">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white/75 sm:text-[10px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-health-soft" />
                  AJMUS Product Demonstrations
                </div>
                <h2 id="ajmus-demo-title" className="mt-3 font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">
                  Live Demo
                </h2>
                <p className="mt-2 max-w-2xl text-xs leading-5 text-white/70 sm:text-sm sm:leading-6">
                  Choose an available AJMUS product to explore now, or contact us for a guided walkthrough tailored to your organisation.
                </p>
              </div>
            </div>

            <div ref={contentRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {DEMO_PRODUCTS.map((product) => (
                  <article
                    key={product.key}
                    className={`relative flex min-h-[230px] flex-col rounded-2xl border p-4 transition ${
                      product.available
                        ? "border-health/20 bg-white shadow-[0_10px_28px_rgba(13,148,136,0.07)]"
                        : "border-black/[0.06] bg-[#F7F8FA]"
                    }`}
                  >
                    <div className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] ${
                      product.available ? "bg-health/10 text-health" : "bg-black/[0.05] text-ajmus-steel"
                    }`}>
                      {product.available ? <CheckCircle2 size={11} /> : <Clock3 size={11} />}
                      {product.status}
                    </div>
                    <h3 className="mt-3 font-heading text-lg font-bold leading-snug tracking-tight text-ajmus-ink">{product.name}</h3>
                    <p className="mt-1.5 flex-1 text-[13px] leading-5 text-ajmus-steel">{product.description}</p>
                    {product.available ? (
                      <a
                        href={product.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group mt-4 inline-flex items-center justify-between rounded-xl bg-ajmus-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-black"
                      >
                        Open Demo
                        <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </a>
                    ) : (
                      <div className="mt-4 rounded-xl border border-black/[0.06] bg-white px-4 py-2.5 text-center text-sm font-semibold text-ajmus-steel">
                        Coming Soon
                      </div>
                    )}
                  </article>
                ))}
              </div>

              <div className="mt-4 flex flex-col gap-2.5 rounded-xl border border-health/15 bg-health/[0.05] px-4 py-3 text-xs leading-5 text-ajmus-steel sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-2.5">
                  <ShieldCheck size={17} className="mt-0.5 shrink-0 text-health" />
                  <span><strong className="text-ajmus-ink">Demo environment:</strong> use fictional information only. Do not enter real patient, customer, prescription or confidential data.</span>
                </div>
                <a href="mailto:contact@ajmus.com?subject=AJMUS%20Guided%20Demo%20Request" className="shrink-0 font-semibold text-ajmus-navy hover:underline">
                  Request guided demo
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
